import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { NavigationFunction as RootNavigation, Pages } from "../../../Navigation/Routers/root";
import { colors, css, fonts, glyphs, icons, Units, useUnitsComponent} from "../../../Styles";
import { Button, ButtonList, Overlay, PEN_BASIC, PEN_EDIT, ProgressCircle} from "../../";
import { ButtonTypes, penTypes } from "../../../Definitions";
import { LfLocation } from "../../../Services/Agrisys_GqlDataCaller/LocationFeedingCalls/GQLCalls/getLfStructureCall";
import { PanelMaintainAnimal } from "../../../Services/Agrisys_GqlDataCaller/AnimalDataCalls/GQLCalls/maintainAnimalInfoCall";

// TODO
import Last10DaysViewer from "../../curveViewer/animalGraph";
import FeedCurveViewer from "../../curveViewer/feedCurveViewer";
import { useAnimal } from "../../../Services/Agrisys_GqlDataCaller/AnimalDataCalls/useAnimal";
import { useAppSelector } from "../../../Store";
import { Graph_QL } from "../../../Services";
import { useSection } from "../../../Services/Agrisys_GqlDataCaller/LocationFeedingCalls/useSections";
import { useFeedCurves } from "../../../Services/Agrisys_GqlDataCaller/FeedDataCalls/useFeedCurves";

interface penModal_Props {
  activePen?: LfLocation;
}
/**
 * PenModal
 * @param activePenId The pen number.
 * @returns A "div" to place in a modal to show all content from a Location Feeder Pen.
 */
export default function PenModal({ activePen }: penModal_Props) {
  const { t } = useTranslation();
  const navigator = useNavigate();
  const getUnit = useUnitsComponent();
  const [currentPenState, setCurrentPenState] = useState<"EDIT" | "BASIC">("BASIC");
  const [currentFeedview, setCurrentFeedview] = useState<"LAST10DAYS" | "FEEDCURVE">("FEEDCURVE");
  const dirtRef = useRef<PanelMaintainAnimal[]>([]);
  
  const farm = useAppSelector(({ activeData }) => activeData.farm) ?? "";
    const domain = useMemo(()=> (Graph_QL.ObjToString({domain: farm ?? ""})),[farm]);
  const connection = useMemo(() => Graph_QL.create({ domain: farm }), [farm]);
  
  const { data: animalsState, maintainEntries: updateAnimals } = useAnimal(connection);
  const { call: getLfSectionsInfo } = useSection(connection);
  const { call: getFeedCurves, data: feedCurveState } = useFeedCurves(connection);

  useEffect(() => {
    getFeedCurves({fetchAmount: 50, ifFeedCurveBaseInfoOnly: false})
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchedFeedCurves = useMemo(() => {
    return feedCurveState && feedCurveState[domain] ? feedCurveState[domain].data : [];
  },[domain, feedCurveState])

  const activeAnimal = useMemo(() => ({
    animal: activePen?.animals?.[0],
  }), [activePen]);

  // ProgressCircle: Remaining Today
  const RemainingToday: JSX.Element = useMemo(() => {
    const consumptions = activeAnimal.animal?.consumptions?.[0]
    const feedTotal = (consumptions?.total || 0) / 1000
    const feedProgressed = (consumptions?.progressed || 0) / 1000

    return (
      <ProgressCircle
        animation
        header={t("feed.general.remainingToday", {
          defaultValue: "remaining today",
        })}
        total={+(feedTotal).toFixed(2)}
        progressed={+(feedTotal - feedProgressed).toFixed(2)}
        unit={getUnit(Units.kG_grey)}
      />
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeAnimal.animal?.consumptions, t])

  // ProgressCircle: Remaining Today
  const RemainingYesterday: JSX.Element = useMemo(() => {
    const consumptions = activeAnimal.animal?.consumptions?.[1]
    const feedTotal = (consumptions?.total || 0) / 1000
    const feedProgressed = (consumptions?.progressed || 0) / 1000

    return (
      <ProgressCircle
        animation
        header={t("feed.general.remainingYesterday", {
          defaultValue: "remaining yesterday",
        })}
        total={+(feedTotal).toFixed(2)}
        progressed={+(feedTotal - feedProgressed).toFixed(2)}
        unit={getUnit(Units.kG_grey)}
      />
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeAnimal.animal?.consumptions, t])

  // FeedCurve
  const FeedCurve: JSX.Element = useMemo(() => {
    const feedCurve = fetchedFeedCurves.find(feedcurve => feedcurve.feedCurveId === activeAnimal.animal?.feedCurve?.feedCurveId);

    if (!feedCurve) return (
      <p style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkPrimary }}>
        {t("animalPages.animal.noFeedCurve", {defaultValue: "No Feed Curve Found"})}
      </p>
    )
    
    return (
      <FeedCurveViewer
        data={feedCurve}
        showComponentLabels
        showMilestoneLabels
      />
    )
  }, [activeAnimal.animal?.feedCurve?.feedCurveId, fetchedFeedCurves, t])

  // Last10Days
  const Last10Days: JSX.Element = useMemo(() => {
    const last10Days = activeAnimal.animal?.lastTenDay

    if (!last10Days) return (
      <p style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkPrimary }}>
        {t("animalPages.animal.noFeedCurve", {defaultValue: "No Feed Curve Found"})}
      </p>
    )

    return (
      <Last10DaysViewer data={last10Days} />
    )
  }, [activeAnimal.animal?.lastTenDay, t])

  // #region | Pen State Router
  const PenState_Router = () => {
    switch (currentPenState) {
      case "EDIT":
        return (
          <PEN_EDIT
            dirtyDataRef={dirtRef}
            pen={activePen}
            penType={penTypes.simple}
            borderRadius={{ borderRadius: "10px" }}
          />
        );
      case "BASIC":
        return (
          <PEN_BASIC
            pen={activePen}
            penType={penTypes.simple}
            borderRadius={{ borderRadius: "10px" }}
          />
        )
      default:
        return null;
    }
  };
  // #endregion

  // #region | Feed View State Router
  const FeedviewStateRouter = () => {
    switch (currentFeedview) {
      case "LAST10DAYS":
        return (
          <div style={{ marginTop: "20px" }}>
            {Last10Days}
          </div>
        );
      case "FEEDCURVE":
        return (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
            {FeedCurve}
          </div>
        );
      default:
        return null;
    }
  }
  // #endregion

  // Seperator Line
  const seperatorLine = (
    <div style={{ width: "650px", display: "flex", alignItems: "center", justifyContent: "center", margin: "20px auto"}}>
      <div style={{ width: "600px", height: "2px", backgroundColor: colors.greyTertiary}}/>
    </div>
  );
  // #endregion

  return ( <>
    {(currentPenState !== "BASIC") ? <Overlay overrides={{ color: colors.transparent }} beforeUnload onClickAlert/> : <></>}
    <div style={{ ...css.modalTop }}>
      {/* Activator, Feeder, Motor */}
      {/* <StatusDisplayList OBS: MOTOR mA NOT WORKING ATM
        headers={[t("attentions.attention"), t("locationFeeder.penPopup.feeder"), t("locationFeeder.penPopup.motor")]} 
        values={["• " + attention ,"• " + feeder, motor + " mA"]}              
        icons={[glyphs.Attention({fillColor: colors.greyPrimary, size: "40px"}), glyphs.Attention({fillColor: colors.greyPrimary, size: "40px"}), glyphs.Attention({fillColor: colors.greyPrimary, size: "40px"})]}
        valueColor={[ _attentionColor(attentionColor), _feederColor(feederColor), colors.greyDarkPrimary]}
      /> */}
      {RemainingToday}
      {RemainingYesterday}
    </div>
    {seperatorLine}

    {/* Activations, Last Activation OBS: NOT WORKING ATM */}
    {/* {hasActivator? <>
      <div style={{display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center", gap: "40px"}}>
        <StatusDisplay
          header={t("locationFeeder.penPopup.activator")} 
          value={"XXXXXX"}              
          // icon={icons.activatorLF}
          icon={glyphs.Attention({fillColor: colors.greyPrimary, size: "40px"})}
          valueColor={colors.greyDarkPrimary}
        />
        < Activations penId={activePenId ?? 0} showActivations showLastActivated/> 
      </div> 
      {seperatorLine}
    </> : <></>} */}

    <div style={{ display: "flex", justifyContent: "center", flexDirection: "column", alignItems: "center"}}>
      {activeAnimal.animal ? ( <>
        
        {/* In this Pen */}
        <div style={{ display: "flex", gap: "10px" }}>
          <img src={icons.animalsColor} style={{width: "45px", height: "45px"}} alt={""}/>
          <div style={{display: "flex", flexDirection: "column", alignItems: "start", justifyContent: "center", gap: "2px"}}>
            <p style={{color: colors.greyPrimary, ...fonts.fontSize4, ...fonts.header}}>{t("locationFeederPages.sections.inThisPen", {defaultValue: "In this Pen"})}</p>
            <p style={{color: colors.greyDarkPrimary, ...fonts.fontSize3, ...fonts.header}}>{`${t("animalPages.animal.animal", {defaultValue: "Animal"})} ${activeAnimal.animal.animal_id}`}</p>
          </div>
        </div>

        {/* Pen/PenEdit based on PenMode */}
        <div style={{marginBottom: "20px"}}>
          <div style={{
            display: "flex",
            flexDirection: "row",
            padding: "0px 20px 0px 16px",
            justifyContent: (currentPenState === "EDIT") ? "end" : "space-between",
            alignItems: "end",
            zIndex: 10000,
            position: "relative",
          }}> 
            {(currentPenState === "BASIC") ? ( <>
              <p style={{ ...css.penHeaders }}>
                {t("locationFeederPages.sections.penValueHeaders", { defaultValue: "Hold the mouse on a value to see column type" })}
              </p>
              <ButtonList
                contents={[
                  t("buttons.details", { defaultValue: "details" }),
                  t("buttons.edit", { defaultValue: "edit" }),
                ]}
                onClick={(i) => {
                  if (i === 0) { if (window.confirm(`${t("locationFeederPages.sections.redirect", {defaultValue: "This will redirect you to the animal page."})}`)) {
                    RootNavigation(navigator, Pages.animals, Pages.animal, true, {
                      selected: activeAnimal.animal?.animal_id,
                    });
                  }}
                  if (i === 1) {setCurrentPenState("EDIT")}
                }}
                buttonType={ButtonTypes.tableButton}
                startEnhancers={[
                  glyphs.Animal({ fillColor: colors.greyDarkPrimary }),
                  glyphs.Edit({ fillColor: colors.greyDarkPrimary }),
                ]}
                overrides={{
                  baseButton: { backgroundColor: colors.greyLightPrimary },
                  hoverStyles: { backgroundColor: colors.greyLightSecondary },
                  contentStyles: { color: colors.greyDarkPrimary },
                }}
              />
            </>) : (
              <ButtonList
                contents={[
                  t("buttons.save", { defaultValue: "save" }),
                  t("buttons.cancel", { defaultValue: "cancel" }),
                  t("buttons.details", { defaultValue: "details" }),
                  t("buttons.edit", { defaultValue: "edit" }),
                ]}
                onClick={(i) => {
                  if (i === 0) {
                    if (updateAnimals && activePen?.location_id) {
                      updateAnimals({ data: dirtRef.current }, {}, {}, () => getLfSectionsInfo({ ids: [1] }, connection));
                    }
                    dirtRef.current = [];
                    setCurrentPenState("BASIC")
                  }
                  if (i === 1) {setCurrentPenState("BASIC")}
                  if (i === 2) {}
                  if (i === 3) {}
                }}
                buttonType={ButtonTypes.tableButton}
                startEnhancers={[
                  glyphs.Check({ fillColor: colors.greyDarkPrimary }),
                  glyphs.Close({ fillColor: colors.greyDarkPrimary }),
                  glyphs.Animal({ fillColor: colors.greyDarkPrimary }),
                  glyphs.Edit({ fillColor: colors.greyDarkPrimary }),
                ]}
                disabled={[false, false, true, true]}
                overrides={(index) => ({
                  contentStyles: { color: colors.greyDarkPrimary },
                  baseButton: {
                    backgroundColor: index === 3 ? colors.greyLightSecondary : colors.greyLightPrimary,
                  },
                  disabledStyles: {
                    opacity: index === 3 ? 1 : 0.5,
                  },
                  hoverStyles: {
                    backgroundColor: colors.greyLightSecondary,
                  },
                })}
              />
            )}
          </div>

          <div style={{ zIndex: 10000, position: "relative" }}>
            {PenState_Router()}
          </div>
        </div>

        {/* FEED VIEW */}
        <ButtonList
          hasActiveLoad
          contents={[
            t("animalPages.animal.feedCurve", {defaultValue: "Feed Curve"}),
            t("feed.general.feedLastDays", { defaultValue: "last 10 days" }),
          ]}
          onClick={(i) => {
            if (i === 0) {setCurrentFeedview("FEEDCURVE")}
            if (i === 1) {setCurrentFeedview("LAST10DAYS")}
          }}
          overrides={{baseButton: { ...css.button_FitContent }}}
        />
        {FeedviewStateRouter()}
      </> )
        
      // IF NO ANIMAL
      : (
        <p style={{ ...fonts.header, ...fonts.fontSize3, color: colors.greyDarkPrimary, marginBottom: "10px"}}>
          {t("locationFeederPages.sections.emptyPen", {defaultValue: "Empty pen"})}
        </p>
      )}
    </div>
  </> );
}
